import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Plus, Trash2, Edit3, ShoppingCart, FileText, Check, X, Package, Building2, Clock, CheckCircle, AlertCircle, Download, Upload } from 'lucide-react';

const STATUS_MAP = {
  rascunho: { label: 'Rascunho', cls: 'badge-neutral', icon: FileText },
  pendente_cotacao: { label: 'Aguarda Cotacao', cls: 'badge-warning', icon: Clock },
  cotacao_recebida: { label: 'Cotacao Recebida', cls: 'badge-info', icon: Package },
  aprovado: { label: 'Aprovado', cls: 'badge-success', icon: Check },
  encomendado: { label: 'Encomendado', cls: 'badge-info', icon: ShoppingCart },
  recebido: { label: 'Recebido', cls: 'badge-success', icon: CheckCircle },
  cancelado: { label: 'Cancelado', cls: 'badge-error', icon: X }
};

function PurchaseForm({ purchase, projects, suppliers, materials, onSave, onCancel }) {
  const [form, setForm] = useState(purchase || {
    reference: '',
    description: '',
    items: [],
    status: 'rascunho',
    supplier_id: '',
    supplier_name: '',
    notes: ''
  });
  const [itemForm, setItemForm] = useState({ material_id: '', material_name: '', quantity: 0, unit: 'un', unit_price: 0, project_id: '', project_name: '' });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const addItem = () => {
    if (!itemForm.material_name || !itemForm.quantity) return;
    const proj = projects.find(p => p.id === itemForm.project_id);
    setForm(p => ({
      ...p,
      items: [...(p.items || []), { 
        ...itemForm, 
        id: Date.now().toString(),
        project_name: proj?.name || '',
        subtotal: itemForm.quantity * itemForm.unit_price
      }]
    }));
    setItemForm({ material_id: '', material_name: '', quantity: 0, unit: 'un', unit_price: 0, project_id: '', project_name: '' });
  };

  const removeItem = (id) => {
    setForm(p => ({ ...p, items: p.items.filter(i => i.id !== id) }));
  };

  const calcTotal = (items) => items.reduce((s, i) => s + (i.quantity || 0) * (i.unit_price || 0), 0);

  return (
    <div className="pro-card-padded animate-in">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold">{purchase ? 'Editar Compra' : 'Nova Requisicao de Compra'}</h2>
        <button onClick={onCancel} className="btn btn-outline btn-sm">Cancelar</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div>
          <label className="field-label">Referencia</label>
          <input className="input" placeholder="REQ-001" value={form.reference} onChange={e => set('reference', e.target.value)} />
        </div>
        <div className="md:col-span-2">
          <label className="field-label">Descricao</label>
          <input className="input" placeholder="Descricao da compra..." value={form.description} onChange={e => set('description', e.target.value)} />
        </div>
      </div>

      {/* Add Items */}
      <div className="p-4 rounded-md mb-6" style={{ background: 'var(--surface-alt)', border: '1px solid var(--border-light)' }}>
        <h3 className="text-sm font-semibold mb-3">Adicionar Item</h3>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-2">
          <div className="col-span-2">
            <select 
              className="select w-full" 
              value={itemForm.material_id}
              onChange={e => {
                const mat = materials.find(m => m.id === e.target.value);
                setItemForm(p => ({ 
                  ...p, 
                  material_id: e.target.value, 
                  material_name: mat?.name || '',
                  unit: mat?.unit || 'un',
                  unit_price: mat?.price_per_unit || 0
                }));
              }}
            >
              <option value="">Selecionar Material...</option>
              {materials.map(m => (
                <option key={m.id} value={m.id}>{m.name} ({m.category})</option>
              ))}
            </select>
          </div>
          <input 
            className="input" 
            type="number" 
            placeholder="Qtd" 
            value={itemForm.quantity || ''} 
            onChange={e => setItemForm(p => ({ ...p, quantity: parseFloat(e.target.value) || 0 }))} 
          />
          <input 
            className="input" 
            placeholder="Unidade" 
            value={itemForm.unit} 
            onChange={e => setItemForm(p => ({ ...p, unit: e.target.value }))} 
          />
          <input 
            className="input" 
            type="number" 
            placeholder="Preco unit." 
            value={itemForm.unit_price || ''} 
            onChange={e => setItemForm(p => ({ ...p, unit_price: parseFloat(e.target.value) || 0 }))} 
          />
          <select 
            className="select" 
            value={itemForm.project_id}
            onChange={e => setItemForm(p => ({ ...p, project_id: e.target.value }))}
          >
            <option value="">Obra destino...</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        <button onClick={addItem} className="btn btn-primary btn-sm"><Plus size={14} /> Adicionar Item</button>
      </div>

      {/* Items List */}
      {form.items?.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold mb-2">Itens ({form.items.length})</h3>
          <table className="pro-table">
            <thead>
              <tr>
                <th>Material</th>
                <th>Qtd</th>
                <th>Unidade</th>
                <th>Preco Unit.</th>
                <th>Subtotal</th>
                <th>Obra</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {form.items.map(i => (
                <tr key={i.id}>
                  <td className="font-medium">{i.material_name}</td>
                  <td>{i.quantity}</td>
                  <td>{i.unit}</td>
                  <td>{i.unit_price?.toLocaleString('pt-PT')} EUR</td>
                  <td className="font-semibold">{((i.quantity || 0) * (i.unit_price || 0)).toLocaleString('pt-PT')} EUR</td>
                  <td><span className="badge badge-neutral">{i.project_name || '-'}</span></td>
                  <td>
                    <button onClick={() => removeItem(i.id)} className="btn btn-danger btn-xs"><X size={12} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="4" className="text-right font-semibold">Total:</td>
                <td className="font-bold text-lg" style={{ color: 'var(--primary)' }}>{calcTotal(form.items).toLocaleString('pt-PT')} EUR</td>
                <td colSpan="2"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="field-label">Fornecedor Preferencial</label>
          <select 
            className="select w-full" 
            value={form.supplier_id}
            onChange={e => {
              const sup = suppliers.find(s => s.id === e.target.value);
              set('supplier_id', e.target.value);
              set('supplier_name', sup?.name || '');
            }}
          >
            <option value="">Selecionar fornecedor...</option>
            {suppliers.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="field-label">Notas</label>
          <input className="input" placeholder="Notas internas..." value={form.notes} onChange={e => set('notes', e.target.value)} />
        </div>
      </div>

      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="btn btn-outline">Cancelar</button>
        <button 
          onClick={() => onSave({ ...form, total: calcTotal(form.items) })} 
          className="btn btn-primary"
          disabled={!form.items?.length}
        >
          Guardar Requisicao
        </button>
      </div>
    </div>
  );
}

function PurchaseDetail({ purchase, projects, suppliers, onBack, onRefresh }) {
  const [showAddQuotation, setShowAddQuotation] = useState(false);
  const [quotationForm, setQuotationForm] = useState({
    supplier_id: '',
    supplier_name: '',
    items: [],
    total: 0,
    valid_until: '',
    notes: '',
    pdf_file: ''
  });

  const status = STATUS_MAP[purchase.status] || STATUS_MAP.rascunho;

  const handleAddQuotation = async () => {
    try {
      await api.post(`/purchases/${purchase.id}/quotations`, quotationForm);
      setShowAddQuotation(false);
      setQuotationForm({ supplier_id: '', supplier_name: '', items: [], total: 0, valid_until: '', notes: '', pdf_file: '' });
      onRefresh();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao adicionar cotacao');
    }
  };

  const handleApprove = async (quotationId) => {
    if (!window.confirm('Aprovar esta cotacao e compra?')) return;
    try {
      await api.post(`/purchases/${purchase.id}/approve`, { quotation_id: quotationId });
      onRefresh();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao aprovar');
    }
  };

  const handleReceive = async () => {
    const invoiceRef = window.prompt('Numero da fatura:');
    if (!invoiceRef) return;
    try {
      await api.post(`/purchases/${purchase.id}/receive`, { invoice_ref: invoiceRef });
      onRefresh();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao receber');
    }
  };

  return (
    <div className="animate-in pt-10 lg:pt-0">
      <div className="flex items-center justify-between mb-4">
        <div>
          <button onClick={onBack} className="text-xs font-medium cursor-pointer mb-1" style={{ color: 'var(--text-light)', background: 'none', border: 'none' }}>← Voltar</button>
          <h1 className="text-xl font-bold">{purchase.reference || 'Sem referencia'}</h1>
          <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{purchase.description}</p>
        </div>
        <span className={`badge ${status.cls}`}>{status.label}</span>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="metric-card">
          <p className="metric-label">Total Estimado</p>
          <p className="metric-value text-lg">{(purchase.total || 0).toLocaleString('pt-PT')} EUR</p>
        </div>
        <div className="metric-card">
          <p className="metric-label">Itens</p>
          <p className="metric-value text-lg">{purchase.items?.length || 0}</p>
        </div>
        <div className="metric-card">
          <p className="metric-label">Cotacoes</p>
          <p className="metric-value text-lg">{purchase.quotations?.length || 0}</p>
        </div>
        <div className="metric-card">
          <p className="metric-label">Criado por</p>
          <p className="metric-value text-sm">{purchase.created_by || '-'}</p>
        </div>
      </div>

      {/* Items */}
      <div className="pro-card-padded mb-4">
        <h3 className="text-sm font-semibold mb-3">Itens da Requisicao</h3>
        {purchase.items?.length > 0 ? (
          <table className="pro-table">
            <thead>
              <tr>
                <th>Material</th>
                <th>Qtd</th>
                <th>Unidade</th>
                <th>Preco Est.</th>
                <th>Subtotal</th>
                <th>Obra</th>
              </tr>
            </thead>
            <tbody>
              {purchase.items.map((i, idx) => (
                <tr key={i.id || idx}>
                  <td className="font-medium">{i.material_name}</td>
                  <td>{i.quantity}</td>
                  <td>{i.unit}</td>
                  <td>{i.unit_price?.toLocaleString('pt-PT')} EUR</td>
                  <td className="font-semibold">{((i.quantity || 0) * (i.unit_price || 0)).toLocaleString('pt-PT')} EUR</td>
                  <td><span className="badge badge-neutral">{i.project_name || '-'}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <p className="text-xs" style={{ color: 'var(--text-light)' }}>Sem itens</p>}
      </div>

      {/* Quotations */}
      <div className="pro-card-padded mb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold">Cotacoes Recebidas</h3>
          {purchase.status !== 'recebido' && purchase.status !== 'cancelado' && (
            <button onClick={() => setShowAddQuotation(true)} className="btn btn-primary btn-sm">
              <Plus size={14} /> Adicionar Cotacao
            </button>
          )}
        </div>

        {showAddQuotation && (
          <div className="p-4 rounded-md mb-4" style={{ background: 'var(--surface-alt)', border: '1px solid var(--border-light)' }}>
            <h4 className="text-xs font-semibold mb-3">Nova Cotacao</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <select
                className="select"
                value={quotationForm.supplier_id}
                onChange={e => {
                  const sup = suppliers.find(s => s.id === e.target.value);
                  setQuotationForm(p => ({ ...p, supplier_id: e.target.value, supplier_name: sup?.name || '' }));
                }}
              >
                <option value="">Selecionar Fornecedor...</option>
                {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <input
                className="input"
                type="number"
                placeholder="Total da cotacao"
                value={quotationForm.total || ''}
                onChange={e => setQuotationForm(p => ({ ...p, total: parseFloat(e.target.value) || 0 }))}
              />
              <input
                className="input"
                type="date"
                placeholder="Valido ate"
                value={quotationForm.valid_until}
                onChange={e => setQuotationForm(p => ({ ...p, valid_until: e.target.value }))}
              />
            </div>
            <input
              className="input mb-3"
              placeholder="Notas da cotacao..."
              value={quotationForm.notes}
              onChange={e => setQuotationForm(p => ({ ...p, notes: e.target.value }))}
            />
            <div className="flex gap-2">
              <button onClick={handleAddQuotation} className="btn btn-primary btn-sm">Guardar Cotacao</button>
              <button onClick={() => setShowAddQuotation(false)} className="btn btn-outline btn-sm">Cancelar</button>
            </div>
          </div>
        )}

        {purchase.quotations?.length > 0 ? (
          <div className="space-y-2">
            {purchase.quotations.map(q => (
              <div 
                key={q.id} 
                className={`p-3 rounded-md ${purchase.selected_quotation_id === q.id ? 'ring-2 ring-green-500' : ''}`}
                style={{ background: 'var(--surface-alt)', border: '1px solid var(--border-light)' }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold">{q.supplier_name}</p>
                    <p className="text-xs" style={{ color: 'var(--text-light)' }}>
                      Data: {q.date} {q.valid_until && `| Valido ate: ${q.valid_until}`}
                    </p>
                    {q.notes && <p className="text-xs mt-1">{q.notes}</p>}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold" style={{ color: 'var(--primary)' }}>{q.total?.toLocaleString('pt-PT')} EUR</span>
                    {purchase.status === 'cotacao_recebida' && (
                      <button onClick={() => handleApprove(q.id)} className="btn btn-success btn-sm">
                        <Check size={14} /> Aprovar
                      </button>
                    )}
                    {purchase.selected_quotation_id === q.id && (
                      <span className="badge badge-success">Selecionada</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : <p className="text-xs" style={{ color: 'var(--text-light)' }}>Sem cotacoes recebidas</p>}
      </div>

      {/* Actions */}
      {purchase.status === 'aprovado' && (
        <div className="pro-card-padded">
          <h3 className="text-sm font-semibold mb-3">Acoes</h3>
          <button onClick={handleReceive} className="btn btn-success">
            <CheckCircle size={16} /> Marcar como Recebido (e distribuir custos)
          </button>
          <p className="text-xs mt-2" style={{ color: 'var(--text-light)' }}>
            Ao marcar como recebido, os custos serao distribuidos automaticamente para as obras associadas.
          </p>
        </div>
      )}

      {/* Invoice Info */}
      {purchase.status === 'recebido' && (
        <div className="pro-card-padded">
          <h3 className="text-sm font-semibold mb-3">Informacao da Fatura</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <p className="text-[10px]" style={{ color: 'var(--text-light)' }}>Ref. Fatura</p>
              <p className="text-sm font-semibold">{purchase.invoice_ref || '-'}</p>
            </div>
            <div>
              <p className="text-[10px]" style={{ color: 'var(--text-light)' }}>Data Fatura</p>
              <p className="text-sm font-semibold">{purchase.invoice_date || '-'}</p>
            </div>
            <div>
              <p className="text-[10px]" style={{ color: 'var(--text-light)' }}>Data Entrega</p>
              <p className="text-sm font-semibold">{purchase.delivery_date || '-'}</p>
            </div>
            <div>
              <p className="text-[10px]" style={{ color: 'var(--text-light)' }}>Aprovado por</p>
              <p className="text-sm font-semibold">{purchase.approved_by || '-'}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PurchasesPage() {
  const [purchases, setPurchases] = useState([]);
  const [projects, setProjects] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');

  const load = useCallback(async () => {
    try {
      const [purRes, projRes, supRes, matRes] = await Promise.all([
        api.get('/purchases'),
        api.get('/projects'),
        api.get('/suppliers'),
        api.get('/materials')
      ]);
      setPurchases(purRes.data || []);
      setProjects(projRes.data || []);
      setSuppliers(supRes.data || []);
      setMaterials(matRes.data || []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (form) => {
    try {
      if (editing) {
        await api.put(`/purchases/${editing.id}`, form);
      } else {
        await api.post('/purchases', form);
      }
      setEditing(null);
      setCreating(false);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao guardar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Apagar requisicao de compra?')) return;
    try {
      await api.delete(`/purchases/${id}`);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao apagar');
    }
  };

  const handleView = async (pur) => {
    try {
      const res = await api.get(`/purchases/${pur.id}`);
      setViewing(res.data);
    } catch (err) {
      alert('Erro ao carregar detalhes');
    }
  };

  if (viewing) {
    return (
      <PurchaseDetail
        purchase={viewing}
        projects={projects}
        suppliers={suppliers}
        onBack={() => { setViewing(null); load(); }}
        onRefresh={() => handleView(viewing)}
      />
    );
  }

  if (creating || editing) {
    return (
      <PurchaseForm
        purchase={editing}
        projects={projects}
        suppliers={suppliers}
        materials={materials}
        onSave={handleSave}
        onCancel={() => { setCreating(false); setEditing(null); }}
      />
    );
  }

  const filtered = purchases.filter(p => !filterStatus || p.status === filterStatus);

  return (
    <div className="animate-in pt-10 lg:pt-0">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <h1 className="text-xl font-bold">Gestao de Compras</h1>
        <button data-testid="new-purchase-btn" onClick={() => setCreating(true)} className="btn btn-primary">
          <Plus size={16} /> Nova Requisicao
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <div className="metric-card">
          <p className="metric-label">Total Requisicoes</p>
          <p className="metric-value">{purchases.length}</p>
        </div>
        <div className="metric-card">
          <p className="metric-label">Pendentes</p>
          <p className="metric-value" style={{ color: 'var(--warning)' }}>
            {purchases.filter(p => ['rascunho', 'pendente_cotacao', 'cotacao_recebida'].includes(p.status)).length}
          </p>
        </div>
        <div className="metric-card">
          <p className="metric-label">Aprovadas</p>
          <p className="metric-value" style={{ color: 'var(--success)' }}>
            {purchases.filter(p => p.status === 'aprovado').length}
          </p>
        </div>
        <div className="metric-card">
          <p className="metric-label">Recebidas</p>
          <p className="metric-value" style={{ color: 'var(--info)' }}>
            {purchases.filter(p => p.status === 'recebido').length}
          </p>
        </div>
        <div className="metric-card">
          <p className="metric-label">Valor Total</p>
          <p className="metric-value">{purchases.reduce((s, p) => s + (p.total || 0), 0).toLocaleString('pt-PT')} EUR</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-4">
        <select className="select" style={{ width: 'auto' }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">Todos os estados</option>
          {Object.entries(STATUS_MAP).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
      </div>

      {/* List */}
      {filtered.length > 0 ? (
        <div className="space-y-2">
          {filtered.map(p => {
            const status = STATUS_MAP[p.status] || STATUS_MAP.rascunho;
            return (
              <div 
                key={p.id} 
                className="pro-card p-4 cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => handleView(p)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-semibold">{p.reference || 'Sem ref.'}</p>
                      <span className={`badge ${status.cls}`}>{status.label}</span>
                    </div>
                    <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{p.description || 'Sem descricao'}</p>
                    <p className="text-[10px] mt-1" style={{ color: 'var(--text-light)' }}>
                      {p.items?.length || 0} itens | {p.supplier_name || 'Sem fornecedor'} | {p.created_at?.split('T')[0]}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold" style={{ color: 'var(--primary)' }}>{(p.total || 0).toLocaleString('pt-PT')} EUR</span>
                    <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                      <button onClick={() => setEditing(p)} className="btn btn-outline btn-xs"><Edit3 size={12} /></button>
                      <button onClick={() => handleDelete(p.id)} className="btn btn-danger btn-xs"><Trash2 size={12} /></button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="pro-card-padded text-center py-8">
          <ShoppingCart size={40} className="mx-auto mb-2" style={{ color: 'var(--text-light)' }} />
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Nenhuma requisicao de compra encontrada</p>
          <button onClick={() => setCreating(true)} className="btn btn-primary mt-3">
            <Plus size={16} /> Criar Primeira Requisicao
          </button>
        </div>
      )}
    </div>
  );
}
